import { useEffect, useMemo, useState } from 'react';
import { BellOff, Shield, X } from 'lucide-react';
import { ADMIN_ONLY_NOTIFICATION_TYPES, type NotificationPreferences } from '@missions/types';
import { Button, Select, useDialogs, useToast } from '@missions/ui';
import { Layer } from '../../components/Layer';
import { api } from '../../lib/client';
import { useApi, useErrorToast, useInvalidate } from '../../lib/hooks';
import { pushPermission } from '../../lib/push';
import { useSession } from '../../lib/session';
import { NOTIF_TYPE } from './NotificationList';

const TIMES = ['07:00', '08:00', '08:30', '09:00', '18:00', '20:00', '21:00', '22:00'];
const timeLabel = (t: string) => {
  const [h, m] = t.split(':').map(Number) as [number, number];
  const d = new Date(2026, 0, 1, h, m);
  return d.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const Switch = ({ checked, onChange, disabled, label }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string }) => (
  <label className="swl">
    <input type="checkbox" className="sr" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} aria-label={label} />
    <span className="sw" />
  </label>
);

/** Per-type × channel matrix, quiet hours, digest (RBAC: admin-only rows hidden for members). */
export function PrefsEditor({ value, onChange }: { value: NotificationPreferences; onChange: (v: NotificationPreferences) => void }) {
  const s = useSession();
  const pushOk = pushPermission() === 'granted';
  const rows = value.rows.filter((r) => s.isAdmin || !ADMIN_ONLY_NOTIFICATION_TYPES.includes(r.type));
  const set = (type: string, ch: 'inApp' | 'push' | 'email', v: boolean) =>
    onChange({ ...value, rows: value.rows.map((r) => (r.type === type ? { ...r, [ch]: v } : r)) });
  return (
    <>
      {!pushOk && (
        <div className="pnote">
          <BellOff />
          <span>Push is off on this device, so push options are disabled. Turn it on from the notification panel.</span>
        </div>
      )}
      <table className="ptable">
        <thead>
          <tr><th>Notify me about</th><th>In-app</th><th>Push</th><th>Email</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const meta = NOTIF_TYPE[r.type];
            const admin = ADMIN_ONLY_NOTIFICATION_TYPES.includes(r.type);
            return (
              <tr key={r.type}>
                <td>
                  <b>
                    {meta.label}
                    {admin && <span className="ni-role" style={{ fontSize: '0.6875rem' }}><Shield />Admins</span>}
                  </b>
                  {r.locked && <small>Your admin keeps this on in-app</small>}
                </td>
                <td><Switch checked={r.inApp} disabled={r.locked} onChange={(v) => set(r.type, 'inApp', v)} label={`In-app for ${meta.label}`} /></td>
                <td><Switch checked={r.push} disabled={!pushOk} onChange={(v) => set(r.type, 'push', v)} label={`Push for ${meta.label}`} /></td>
                <td><Switch checked={r.email} onChange={(v) => set(r.type, 'email', v)} label={`Email for ${meta.label}`} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="prow2">
        <div>
          <span className="lbl">Daily email digest</span>
          <Select
            value={value.digest.enabled ? value.digest.time : 'off'}
            onChange={(v) => onChange({ ...value, digest: v === 'off' || !v ? { ...value.digest, enabled: false } : { enabled: true, time: v } })}
            options={[{ value: 'off', label: 'Off' }, ...TIMES.slice(0, 4).map((t) => ({ value: t, label: `Every weekday at ${timeLabel(t)}` }))]}
            aria-label="Daily digest"
            title="Daily digest"
          />
        </div>
        <div>
          <span className="lbl">Quiet hours (no push)</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Select value={value.quietHours.from} onChange={(v) => v && onChange({ ...value, quietHours: { ...value.quietHours, enabled: true, from: v } })} options={TIMES.map((t) => ({ value: t, label: timeLabel(t) }))} aria-label="Quiet hours from" title="From" />
            <span style={{ color: 'var(--text-3)' }}>to</span>
            <Select value={value.quietHours.to} onChange={(v) => v && onChange({ ...value, quietHours: { ...value.quietHours, enabled: true, to: v } })} options={TIMES.map((t) => ({ value: t, label: timeLabel(t) }))} aria-label="Quiet hours to" title="To" />
          </div>
        </div>
      </div>
      <label className="swl" style={{ marginTop: 16 }}>
        <input type="checkbox" className="sr" checked={value.quietHours.enabled} onChange={(e) => onChange({ ...value, quietHours: { ...value.quietHours, enabled: e.target.checked } })} />
        <span className="sw" />
        Quiet hours on
      </label>
    </>
  );
}

export function PrefsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const q = useApi('notifications.preferences', undefined, { enabled: open });
  const [draft, setDraft] = useState<NotificationPreferences | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const dialogs = useDialogs();
  const onErr = useErrorToast();
  const invalidate = useInvalidate();
  useEffect(() => {
    if (open && q.data) setDraft(q.data);
  }, [open, q.data]);
  const dirty = useMemo(() => !!draft && !!q.data && JSON.stringify(draft) !== JSON.stringify(q.data), [draft, q.data]);
  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await api.call('notifications.updatePreferences', { body: draft });
      await invalidate('notifications.preferences');
      toast('Notification preferences saved');
      onClose();
    } catch (e) {
      onErr(e);
    } finally {
      setSaving(false);
    }
  };
  const requestClose = async () => {
    if (!dirty) return onClose();
    const r = await dialogs.choice({
      title: 'You have unsaved changes',
      tone: 'warning',
      message: 'Save them before you leave, or discard them. Discarded changes can’t be recovered.',
      actions: [
        { label: 'Keep editing', value: 'stay', cancel: true },
        { label: 'Discard', value: 'discard', tone: '' },
        { label: 'Save & close', value: 'save', tone: 'primary' },
      ],
    });
    if (r === 'discard') onClose();
    if (r === 'save') await save();
  };
  return (
    <Layer open={open} onRequestClose={() => void requestClose()} className="modal wide" labelledBy="prefT">
      <form
        style={{ display: 'contents' }}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="m-h">
          <h2 id="prefT">Notification preferences</h2>
          <button type="button" className="ib" onClick={() => void requestClose()} aria-label="Close"><X /></button>
        </div>
        <div className="m-sub">Choose how you hear about each type of update. These settings apply to every workspace you belong to.</div>
        <div className="m-b">
          {draft ? <PrefsEditor value={draft} onChange={setDraft} /> : <div style={{ padding: 20, color: 'var(--text-3)' }}>Loading preferences…</div>}
        </div>
        <div className="m-f">
          <span className="sp" />
          <Button type="button" onClick={() => void requestClose()}>Cancel</Button>
          <Button type="submit" variant="primary" loading={saving} disabled={!dirty}>Save preferences</Button>
        </div>
      </form>
    </Layer>
  );
}
